import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TrendingUp, AlertCircle, CircleUser, Search, X, Sun, Moon, Eye, EyeOff, LayoutDashboard, Zap, ShieldCheck, Gift, BookMarked } from 'lucide-react';
import { CartProvider } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import LeagueSelector from './components/LeagueSelector';
import OddsGrid from './components/OddsGrid';
import ParlayCart from './components/ParlayCart';
import SettingsModal from './components/SettingsModal';
import OpportunityDashboard from './components/OpportunityDashboard';
import GlobalFilters from './components/GlobalFilters';
import GameDetailPage from './components/GameDetailPage';
import HomePage from './components/HomePage';
import LandingPage from './components/LandingPage';
import ToolIntro from './components/ToolIntro';
import PromoTool from './components/PromoTool';
import LoginPage from './components/LoginPage';
import BetHistory from './components/BetHistory';
import SportsbookLogo, { SPORTSBOOK_NAMES } from './components/SportsbookLogo';
import { API_BASE, apiRequest } from './api';
import ErrorBoundary from './components/ErrorBoundary';
import ContactModal from './components/ContactModal';
import './App.css';

const API_BASE_URL = API_BASE;

// Hardcoded filter constants (not user-controllable)
const TIME_HORIZON = '1w';
const MIN_BOOKS = 3;

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <AppContent />
      </CartProvider>
    </AuthProvider>
  );
}

function CompleteProfileModal({ onComplete, session }) {
  const { updateProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [signupBankroll, setSignupBankroll] = useState('1000');
  const [signupBooks, setSignupBooks] = useState([]);
  const [signupAlertEnabled, setSignupAlertEnabled] = useState(false);
  const [signupAlertThreshold, setSignupAlertThreshold] = useState('1.0');
  const [signupAlertAllBooks, setSignupAlertAllBooks] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggleBook = (key) =>
    setSignupBooks(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 1) { setStep(2); return; }
    setLoading(true);
    const bk = Number(signupBankroll) || 1000;
    const { error } = await updateProfile({
      first_name: firstName,
      last_name: lastName,
      phone,
      preferred_bankroll: bk,
      preferred_books: signupBooks,
    });
    if (!error && session) {
      await apiRequest('/user/profile', {
        method: 'PUT',
        body: JSON.stringify({ bankroll: bk, kellyFraction: 0.25, preferredBooks: signupBooks, arbAlertEnabled: signupAlertEnabled, arbAlertThreshold: parseFloat(signupAlertThreshold) || 1.0, arbAlertAllBooks: signupAlertAllBooks }),
      }, session).catch(() => {});
    }
    setLoading(false);
    if (error) {
      setError(error.message);
    } else {
      onComplete(bk, signupBooks, signupAlertEnabled, parseFloat(signupAlertThreshold) || 1.0, signupAlertAllBooks);
    }
  };

  return (
    <div className="login-modal-overlay">
      <div className="login-modal-card">
        <div className="login-brand">
          <div className="login-brand-icon"><TrendingUp size={20} /></div>
          <h1 className="login-title">NavBets</h1>
        </div>
        <p className="login-subtitle">
          {step === 1 ? 'Complete your profile to get started.' : 'Set up your account'}
        </p>
        <form onSubmit={handleSubmit} className="login-form">
          {step === 1 && (
            <>
              <div className="login-name-row">
                <input type="text" placeholder="First name" value={firstName} onChange={e => setFirstName(e.target.value)} required className="login-input" />
                <input type="text" placeholder="Last name" value={lastName} onChange={e => setLastName(e.target.value)} required className="login-input" />
              </div>
              <input type="tel" placeholder="Phone number" value={phone} onChange={e => setPhone(e.target.value)} required className="login-input" />
            </>
          )}
          {step === 2 && (
            <>
              <p className="signup-optional-note">These are optional — you can change them anytime in Settings.</p>
              <div className="signup-bankroll-section">
                <label className="signup-section-label">
                  Starting bankroll
                  <span className="signup-info-tip">
                    i
                    <span className="signup-info-tooltip">Your total betting budget. NavBets uses this to calculate how much to recommend betting on each opportunity.</span>
                  </span>
                </label>
                <div className="signup-bankroll-input-row">
                  <span className="signup-bankroll-symbol">$</span>
                  <input type="number" min="0" step="50" value={signupBankroll} onChange={e => setSignupBankroll(e.target.value)} className="login-input signup-bankroll-input" />
                </div>
              </div>
              <div className="signup-books-section">
                <label className="signup-section-label">Which sportsbooks do you use?</label>
                <div className="signup-books-grid">
                  {Object.entries(SPORTSBOOK_NAMES).map(([key, name]) => (
                    <button key={key} type="button" className={`signup-book-pill${signupBooks.includes(key) ? ' selected' : ''}`} onClick={() => toggleBook(key)}>
                      {name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="signup-alert-section">
                <div className="settings-field settings-field--toggle">
                  <label className="settings-label settings-label--toggle">
                    <span className="signup-section-label" style={{margin: 0}}>Arbitrage email alerts</span>
                    <button
                      type="button"
                      className={`toggle-btn${signupAlertEnabled ? ' toggle-btn--on' : ''}`}
                      onClick={() => setSignupAlertEnabled(v => !v)}
                      aria-pressed={signupAlertEnabled}
                    >
                      <span className="toggle-thumb" />
                    </button>
                  </label>
                </div>
                <p className="signup-alert-note">Get emailed when new arb opportunities arise.</p>
                <p className="signup-alert-note" style={{marginTop: 2}}>Set a minimum profit % so you only get alerted for arbs above that return.</p>
                {signupAlertEnabled && (
                  <div className="signup-alert-sub">
                    <label>
                      Min profit %:
                      <input
                        type="number"
                        value={signupAlertThreshold}
                        onChange={e => setSignupAlertThreshold(e.target.value)}
                        min="0.1"
                        step="0.1"
                      />
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={signupAlertAllBooks}
                        onChange={e => setSignupAlertAllBooks(e.target.checked)}
                      />
                      Include all books (not just my selection)
                    </label>
                  </div>
                )}
              </div>
            </>
          )}
          {error && <p className="login-error">{error}</p>}
          {step === 2 ? (
            <div className="signup-step2-actions">
              <button type="button" className="signup-back-btn" onClick={() => setStep(1)}>← Back</button>
              <button type="submit" className="login-submit-btn" disabled={loading}>
                {loading ? 'Saving...' : 'Save & Continue'}
              </button>
            </div>
          ) : (
            <button type="submit" className="login-submit-btn" disabled={loading}>Next →</button>
          )}
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal() {
  const { updatePassword, clearPasswordRecovery, signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);
    const { error } = await updatePassword(password);
    setLoading(false);
    if (error) { setError(error.message); return; }
    clearPasswordRecovery();
    await signOut();
  };

  return (
    <div className="login-modal-overlay">
      <div className="login-modal-card">
        <div className="login-brand">
          <div className="login-brand-icon"><TrendingUp size={20} /></div>
          <h1 className="login-title">Set new password</h1>
        </div>
        <p className="login-subtitle">Choose a new password for your account.</p>
        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-password-wrap">
            <input type={showPassword ? 'text' : 'password'} placeholder="New password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="login-input" />
            <button type="button" className="login-password-toggle" onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="login-password-wrap">
            <input type={showPassword ? 'text' : 'password'} placeholder="Confirm new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="login-input" />
          </div>
          {error && <p className="login-error">{error}</p>}
          <button type="submit" className="login-submit-btn" disabled={loading}>
            {loading ? 'Saving...' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
}

function AppContent() {
  const { user, session, loading: authLoading, signOut, isPasswordRecovery } = useAuth();

  // Load server-side profile on login to hydrate bankroll/kelly/books
  useEffect(() => {
    if (!user || !session) return;
    const meta = user.user_metadata ?? {};
    apiRequest('/user/profile', {}, session).then(data => {
      if (data.success && data.profile) {
        const serverHasBooks = data.profile.preferredBooks?.length > 0;
        const serverHasBankroll = data.profile.bankroll != null;
        const books = serverHasBooks ? data.profile.preferredBooks : (meta.preferred_books ?? []);
        const bk = serverHasBankroll ? data.profile.bankroll : (meta.preferred_bankroll ?? 1000);
        const kf = data.profile.kellyFraction ?? 0.25;
        setBankroll(bk);
        setKellyFraction(kf);
        setSelectedBooks(books);
        setEvBooks(books);
        setArbBooks(books);
        if (data.profile.arbAlertEnabled != null) setArbAlertEnabled(data.profile.arbAlertEnabled);
        if (data.profile.arbAlertThreshold != null) setArbAlertThreshold(data.profile.arbAlertThreshold);
        if (data.profile.arbAlertAllBooks != null) setArbAlertAllBooks(data.profile.arbAlertAllBooks);
        // First login: sync signup preferences to server profile
        if (!serverHasBooks && !serverHasBankroll && (meta.preferred_books?.length > 0 || meta.preferred_bankroll || meta.arb_alert_enabled)) {
          apiRequest('/user/profile', {
            method: 'PUT',
            body: JSON.stringify({ bankroll: bk, kellyFraction: kf, preferredBooks: books, arbAlertEnabled: !!meta.arb_alert_enabled, arbAlertThreshold: meta.arb_alert_threshold ?? 1.0, arbAlertAllBooks: !!meta.arb_alert_all_books }),
          }, session).catch(() => {});
          if (meta.arb_alert_enabled) setArbAlertEnabled(true);
          if (meta.arb_alert_threshold) setArbAlertThreshold(meta.arb_alert_threshold);
          if (meta.arb_alert_all_books) setArbAlertAllBooks(true);
        }
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const navigate = useNavigate();
  const location = useLocation();

  // Derive current view from URL pathname
  const pathname = location.pathname;
  const currentView = (() => {
    if (pathname === '/') return 'home';
    if (pathname.startsWith('/game/')) return 'game';
    const seg = pathname.split('/')[1];
    return seg || 'home';
  })();

  // Game detail state: eventId from URL, sport/eventData from navigation state
  const gameEventId = pathname.startsWith('/game/') ? pathname.split('/')[2] : null;
  const gameRouteState = location.state?.game || null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSport, setActiveSport] = useState('nba'); // Default sport for browse view
  const [activeLeague, setActiveLeague] = useState(null);
  const minEdge = 3;
  const [liveStatus, setLiveStatus] = useState(() => {
    return localStorage.getItem('liveStatus') || 'all';
  });
  const minProbability = null;  // null = no filter
  const [availableSports, setAvailableSports] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showToolModal, setShowToolModal] = useState(false);
  const [selectedBooks, setSelectedBooks] = useState(() => {
    try { return JSON.parse(localStorage.getItem('selectedBooks') || '[]'); }
    catch { return []; }
  });
  const [prevOdds, setPrevOdds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('prevOdds') || '{}'); }
    catch { return {}; }
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMarket, setActiveMarket] = useState('h2h');
  const [highlightedEventId, setHighlightedEventId] = useState(null);
  const [bankroll, setBankroll] = useState(1000);
  const [kellyFraction, setKellyFraction] = useState(0.25);
  const [arbAlertEnabled, setArbAlertEnabled] = useState(false);
  const [arbAlertThreshold, setArbAlertThreshold] = useState(1.0);
  const [arbAlertAllBooks, setArbAlertAllBooks] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true;
  });

  // Sync dark class on body
  useEffect(() => {
    document.body.classList.toggle('dark', isDark);
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  // Persist selected books so the modal never flickers to "all books" on restart
  useEffect(() => {
    localStorage.setItem('selectedBooks', JSON.stringify(selectedBooks));
  }, [selectedBooks]);

  // Stats for HomePage
  const [homeStats, setHomeStats] = useState({ evCount: 0, arbCount: 0, sampleArb: null, sampleGame: null });

  // EV+ view: per-view sportsbook filter (defaults to settings selection)
  const [evBooks, setEvBooks] = useState([]);
  const toggleEvBook = (key) => {
    setEvBooks(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  // Arbitrage view: per-view sportsbook filter (defaults to settings selection)
  const [arbBooks, setArbBooks] = useState([]);
  const toggleArbBook = (key) => {
    setArbBooks(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  // Persist liveStatus to localStorage
  useEffect(() => {
    localStorage.setItem('liveStatus', liveStatus);
  }, [liveStatus]);

  // Fetch opportunity counts + preview data for landing page
  useEffect(() => {
    if (pathname !== '/') return;

    const fetchStats = async () => {
      try {
        let url = `${API_BASE_URL}/opportunities?max_ev=1&max_arb=1&time_window=all`;
        if (selectedBooks && selectedBooks.length > 0) {
          url += `&books=${selectedBooks.join(',')}`;
        }
        const [oppResult, previewResult] = await Promise.all([
          fetch(url).then(r => r.json()),
          fetch(`${API_BASE_URL}/preview`).then(r => r.json()).catch(() => ({})),
        ]);
        if (oppResult.success) {
          setHomeStats({
            evCount: (oppResult.ev_count || 0) + (oppResult.prop_ev_count || 0),
            arbCount: (oppResult.arb_count || 0) + (oppResult.prop_arb_count || 0),
            sampleArb: previewResult.sample_arb || null,
            sampleGame: previewResult.sample_game || null,
          });
        }
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 60 * 1000);
    return () => clearInterval(interval);
  }, [pathname, selectedBooks]);

  // Fetch available sports on mount
  useEffect(() => {
    const fetchSports = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/sports`);
        const result = await response.json();
        if (result.success) {
          setAvailableSports(result.sports);
        }
      } catch (err) {
        console.error('Error fetching sports:', err);
      }
    };
    fetchSports();
  }, []);

  // Auto-select first available sport when on browse view and current sport isn't in the list
  useEffect(() => {
    if (currentView === 'browse' && availableSports.length > 0) {
      const isAvailable = availableSports.some(s => s.id === activeSport);
      if (!isAvailable) setActiveSport(availableSports[0].id);
    }
  // activeSport intentionally omitted — adding it would cause an infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, availableSports]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      let url;
      if (activeSport === 'live') {
        // Call dashboard with live_status=live for every available sport in parallel —
        // same live detection logic the regular sport tabs use (is_event_live + ESPN state)
        const effectiveMinBooks = 1;
        const responses = await Promise.all(
          (availableSports || []).map(sport => {
            let u = `${API_BASE_URL}/dashboard?sport=${sport.id}&live_status=live&min_books=${effectiveMinBooks}`;
            if (selectedBooks && selectedBooks.length > 0) u += `&books=${selectedBooks.join(',')}`;
            return fetch(u).then(r => r.json()).catch(() => ({ success: false, events: [] }));
          })
        );

        const allEvents = [];
        responses.forEach(result => {
          if (result.success && result.events) allEvents.push(...result.events);
        });
        setData({ success: true, events: allEvents, ev_opportunities: [], arb_opportunities: [] });
        setLoading(false);
        return;
      } else if (activeSport === 'home') {
        url = `${API_BASE_URL}/featured?time_window=${TIME_HORIZON}`;
        if (selectedBooks && selectedBooks.length > 0) {
          url += `&books=${selectedBooks.join(',')}`;
        }
      } else {
        url = `${API_BASE_URL}/dashboard?min_edge=${minEdge}&sport=${activeSport}`;
        if (activeLeague) {
          url += `&league=${activeLeague}`;
        }
        if (minProbability) {
          url += `&min_probability=${minProbability}`;
        }
        if (selectedBooks && selectedBooks.length > 0) {
          url += `&books=${selectedBooks.join(',')}`;
        }
        if (liveStatus && liveStatus !== 'all') {
          url += `&live_status=${liveStatus}`;
        }
        url += `&time_window=${activeSport === 'soccer' ? 'all' : TIME_HORIZON}`;
        const sportMinBooks = ['ufc', 'boxing'].includes(activeSport) ? 2 : MIN_BOOKS;
        const effectiveMinBooks = (selectedBooks && selectedBooks.length > 0)
          ? 1
          : sportMinBooks;
        url += `&min_books=${effectiveMinBooks}`;
      }

      const response = await fetch(url);
      const result = await response.json();

      if (result.success) {
        // Track odds movement: save current odds as "previous" before updating
        if (data && data.events) {
          const currentOdds = {};
          data.events.forEach(event => {
            event.bookmakers.forEach(book => {
              const key = `${event.id}_${book.name}`;
              currentOdds[`${key}_home`] = book.home_odds;
              currentOdds[`${key}_away`] = book.away_odds;
              if (book.draw_odds) {
                currentOdds[`${key}_draw`] = book.draw_odds;
              }
              if (book.home_spread_odds) {
                currentOdds[`${key}_home_spread`] = book.home_spread_odds;
                currentOdds[`${key}_away_spread`] = book.away_spread_odds;
              }
              if (book.over_odds) {
                currentOdds[`${key}_over`] = book.over_odds;
                currentOdds[`${key}_under`] = book.under_odds;
              }
            });
          });
          setPrevOdds(currentOdds);
          localStorage.setItem('prevOdds', JSON.stringify(currentOdds));
        }

        setData(result);
      } else {
        setError('Failed to fetch data');
      }
    } catch (err) {
      setError('Error connecting to server: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Auto-refresh every 60 seconds (matches backend cache interval)
    const interval = setInterval(fetchData, 60 * 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minEdge, minProbability, activeSport, activeLeague, selectedBooks, liveStatus]);

  const handleSportChange = (sportId) => {
    setActiveSport(sportId);
    setActiveLeague(null);
    setActiveMarket('h2h');
    setHighlightedEventId(null);
  };

  const handleViewGame = (eventId, sport) => {
    const eventData = data?.events?.find(e => e.id === eventId) || null;
    navigate(`/game/${eventId}`, { state: { game: { eventId, sport, eventData } } });
  };

  const currentSport = availableSports.find(s => s.id === activeSport);

  const filterEvents = (events) => {
    if (!searchQuery.trim()) return events;
    const q = searchQuery.trim().toLowerCase();
    return events.filter(e =>
      e.home_team.toLowerCase().includes(q) || e.away_team.toLowerCase().includes(q)
    );
  };

  // Handle navigation from HomePage — gate for unauthenticated users
  const handleNavigate = (view) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    if (view === 'browse') setActiveSport(availableSports.length > 0 ? availableSports[0].id : 'nba');
    navigate(view === 'home' ? '/' : `/${view}`);
  };

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showContact, setShowContact] = useState(false);
const [profileSaved, setProfileSaved] = useState(false);
  const needsProfile = user && !user.user_metadata?.phone && !profileSaved;

  // Auto-close login modal when user signs in
  useEffect(() => {
    if (user) setShowLoginModal(false);
  }, [user]);

  // Redirect to home when user signs out
  useEffect(() => {
    if (!authLoading && !user && currentView !== 'home') {
      navigate('/');
    }
  }, [user, authLoading, currentView, navigate]);

  if (authLoading) {
    return <div className="auth-loading"><div className="spinner" /></div>;
  }

  const isLanding = currentView === 'home' && !user;

  return (
    <div className="App">
      <div className={`main-content${isLanding ? ' main-content--landing' : ''}`}>
        {!isLanding && <header className="header">
          <div className="header-content">
            <button className="header-logo" onClick={() => navigate('/')}>
              <img src="/navbets-logo-new.png" alt="NavBets" className="header-logo-img" />
              <span className="header-logo-wordmark">NavBets</span>
            </button>

            {user && (() => {
              const meta = user.user_metadata ?? {};
              const first = (meta.first_name || '').trim();
              const last = (meta.last_name || '').trim();
              const initials = first && last
                ? first[0].toUpperCase() + last[0].toUpperCase()
                : first ? first.slice(0, 2).toUpperCase()
                : (user.email || 'U')[0].toUpperCase();
              const viewTitle = { browse: 'Odds Dashboard', ev: '+EV', arbitrage: 'Arb', promo: 'Promo', bets: 'My Bets', game: 'Game Detail' }[currentView] || '';
              const hasToolIntro = ['browse', 'ev', 'arbitrage', 'promo', 'bets'].includes(currentView);
              return (
                <div className={`header-mobile-title header-mobile-title--${currentView}`}>
                  <span className="header-mobile-title-text">{viewTitle}</span>
                  {hasToolIntro && (
                    <button className="header-mobile-how" onClick={() => setShowToolModal(true)}>
                      How it works →
                    </button>
                  )}
                </div>
              );
            })()}

            {user ? (
              <>
                <nav className="header-nav">
                  <button className={`header-nav-btn${currentView === 'browse' ? ' active active-browse' : ''}`} onClick={() => handleNavigate('browse')}>Odds</button>
                  <button className={`header-nav-btn${currentView === 'ev' ? ' active active-ev' : ''}`} onClick={() => handleNavigate('ev')}>+EV</button>
                  <button className={`header-nav-btn${currentView === 'arbitrage' ? ' active active-arb' : ''}`} onClick={() => handleNavigate('arbitrage')}>Arb</button>
                  <button className={`header-nav-btn${currentView === 'promo' ? ' active active-promo' : ''}`} onClick={() => handleNavigate('promo')}>Promo</button>
                  <button className={`header-nav-btn${currentView === 'bets' ? ' active active-bets' : ''}`} onClick={() => handleNavigate('bets')}>My Bets</button>
                </nav>
                <div className="header-actions">
                  <button className="header-icon-btn" onClick={() => setIsDark(d => !d)} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
                    {isDark ? <Sun size={17} /> : <Moon size={17} />}
                  </button>
                  <button className="header-settings-btn" onClick={() => setShowSettings(true)}>
                    {(() => {
                      const meta = user.user_metadata ?? {};
                      const first = (meta.first_name || '').trim();
                      const last = (meta.last_name || '').trim();
                      const initials = first && last
                        ? first[0].toUpperCase() + last[0].toUpperCase()
                        : first ? first.slice(0, 2).toUpperCase()
                        : (user.email || 'U')[0].toUpperCase();
                      return <span className="header-user-avatar">{initials}</span>;
                    })()}
                    <span className="header-settings-label">Settings</span>
                  </button>
                  <button className="header-logout-btn" onClick={signOut}>Log out</button>
                </div>
              </>
            ) : (
              <div className="header-actions">
                <button className="header-icon-btn" onClick={() => setIsDark(d => !d)} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
                  {isDark ? <Sun size={17} /> : <Moon size={17} />}
                </button>
                <button className="header-auth-btn" onClick={() => setShowLoginModal(true)}>
                  Sign Up / Log In
                </button>
              </div>
            )}
          </div>
        </header>}

        {/* MOBILE BOTTOM NAV — logged-in users only, hidden on desktop */}
        {!isLanding && user && (
          <nav className="mobile-bottom-nav">
            <button className={`mbn-btn${currentView === 'browse' ? ' active active-browse' : ''}`} onClick={() => handleNavigate('browse')}><LayoutDashboard size={18} /><span>Odds</span></button>
            <button className={`mbn-btn${currentView === 'ev' ? ' active active-ev' : ''}`} onClick={() => handleNavigate('ev')}><Zap size={18} /><span>+EV</span></button>
            <button className={`mbn-btn${currentView === 'arbitrage' ? ' active active-arb' : ''}`} onClick={() => handleNavigate('arbitrage')}><ShieldCheck size={18} /><span>Arb</span></button>
            <button className={`mbn-btn${currentView === 'promo' ? ' active active-promo' : ''}`} onClick={() => handleNavigate('promo')}><Gift size={18} /><span>Promo</span></button>
            <button className={`mbn-btn${currentView === 'bets' ? ' active active-bets' : ''}`} onClick={() => handleNavigate('bets')}><BookMarked size={18} /><span>My Bets</span></button>
          </nav>
        )}


        {/* HOME VIEW */}
        {currentView === 'home' && (
          user ? (
            <ErrorBoundary label="Home">
              <HomePage
                onNavigate={handleNavigate}
                stats={homeStats}
                user={user}
                onLoginRequest={() => setShowLoginModal(true)}
              />
            </ErrorBoundary>
          ) : (
            <LandingPage
              onLoginRequest={() => setShowLoginModal(true)}
              stats={homeStats}
              onContact={() => setShowContact(true)}
            />
          )
        )}

        {/* BROWSE ODDS VIEW */}
        {currentView === 'browse' && (
          <ErrorBoundary label="Browse Odds">
          <>
            <ToolIntro toolKey="browse" open={showToolModal} onClose={() => setShowToolModal(false)} />

            {/* Sport Selection Tabs */}
            <SportTabs
              sports={availableSports}
              activeSport={activeSport}
              onChange={handleSportChange}
              showHome={false}
            />

            {/* Global Filters */}
            <GlobalFilters
              liveStatus={liveStatus}
              setLiveStatus={setLiveStatus}
            />

            {/* League Selector */}
            <LeagueSelector
              sport={currentSport}
              activeLeague={activeLeague}
              onChange={setActiveLeague}
            />

            {/* Search */}
            <div className="browse-search">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Search teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="search-clear" onClick={() => setSearchQuery('')}>
                  <X size={16} />
                </button>
              )}
            </div>

            {error && (
              <div className="error-message content">
                <AlertCircle size={20} />
                {error}
              </div>
            )}

            <div className="browse-with-cart">
              <div className="browse-main content">
                {loading && !data && (
                  <div className="loading">Loading...</div>
                )}

                {data && (() => {
                  const filtered = filterEvents(data.events);

                  if (activeSport === 'live' && filtered.length === 0) {
                    return (
                      <div className="live-empty-state">
                        <span className="live-empty-dot" />
                        <div className="live-empty-title">No games live right now</div>
                        <div className="live-empty-sub">Check back when games are in progress — odds update in real time.</div>
                      </div>
                    );
                  }

                  const gridProps = {
                    prevOdds, activeMarket, highlightedEventId,
                    onClearHighlight: () => setHighlightedEventId(null),
                    onSelectGame: (eventId, _market, sport) => handleViewGame(eventId, sport),
                    evOpportunities: data.ev_opportunities || [],
                    arbOpportunities: data.arbitrage_opportunities || [],
                    selectedBooks,
                  };
                  // Group by tournament when viewing a multi-league sport with no league filter
                  if (currentSport?.has_leagues && !activeLeague && filtered.length > 0) {
                    const groups = {};
                    filtered.forEach(event => {
                      const key = event.sport || 'unknown';
                      if (!groups[key]) groups[key] = { title: event.sport_title || key, events: [] };
                      groups[key].events.push(event);
                    });
                    return Object.entries(groups).map(([key, { title, events }]) => (
                      <div key={key} className="tournament-group">
                        <div className="tournament-group-header">{title}</div>
                        <OddsGrid events={events} {...gridProps} />
                      </div>
                    ));
                  }
                  return <OddsGrid events={filtered} {...gridProps} />;
                })()}
              </div>
              <ParlayCart />
            </div>
          </>
          </ErrorBoundary>
        )}

        {/* EV BETS VIEW */}
        {currentView === 'ev' && (
          <ErrorBoundary label="EV Bets">
            <>
              <ToolIntro toolKey="ev" open={showToolModal} onClose={() => setShowToolModal(false)} />

              {/* Per-view sportsbook filter */}
              <div className="ev-book-filter">
                <span className="ev-book-filter-label">Books:</span>
                {(selectedBooks.length > 0 ? selectedBooks : Object.keys(SPORTSBOOK_NAMES)).map(key => (
                  <button
                    key={key}
                    className={`ev-book-pill${evBooks.includes(key) ? ' active' : ''}`}
                    onClick={() => toggleEvBook(key)}
                  >
                    <SportsbookLogo bookKey={key} size={14} />
                    {SPORTSBOOK_NAMES[key] || key}
                  </button>
                ))}
              </div>

              <GlobalFilters
                liveStatus={liveStatus}
                setLiveStatus={setLiveStatus}
              />

              <div className="content">
                <OpportunityDashboard
                  selectedBooks={evBooks}
                  timeWindow={TIME_HORIZON}
                  liveStatus={liveStatus}
                  maxOdds={200}
                  bankroll={bankroll}
                  kellyFraction={kellyFraction}
                  onViewGame={(eventId, sport) => handleViewGame(eventId, sport)}
                  onNavigateToSport={(sportId) => {
                    setActiveSport(sportId);
                    navigate('/browse');
                  }}
                  showOnlyEV={true}
                  session={session}
                />
              </div>
            </>
          </ErrorBoundary>
        )}

        {/* ARBITRAGE VIEW */}
        {currentView === 'arbitrage' && (
          <ErrorBoundary label="Arbitrage">
            <>
              <ToolIntro toolKey="arbitrage" open={showToolModal} onClose={() => setShowToolModal(false)} />

              {/* Per-view sportsbook filter */}
              <div className="ev-book-filter">
                <span className="ev-book-filter-label">Books:</span>
                {(selectedBooks.length > 0 ? selectedBooks : Object.keys(SPORTSBOOK_NAMES)).map(key => (
                  <button
                    key={key}
                    className={`ev-book-pill${arbBooks.includes(key) ? ' active' : ''}`}
                    onClick={() => toggleArbBook(key)}
                  >
                    <SportsbookLogo bookKey={key} size={14} />
                    {SPORTSBOOK_NAMES[key] || key}
                  </button>
                ))}
              </div>

              <GlobalFilters
                liveStatus={liveStatus}
                setLiveStatus={setLiveStatus}
              />

              <div className="content">
                <OpportunityDashboard
                  selectedBooks={arbBooks}
                  timeWindow={TIME_HORIZON}
                  liveStatus={liveStatus}
                  bankroll={bankroll}
                  kellyFraction={kellyFraction}
                  onViewGame={(eventId, sport) => handleViewGame(eventId, sport)}
                  onNavigateToSport={(sportId) => {
                    setActiveSport(sportId);
                    navigate('/browse');
                  }}
                  showOnlyArbitrage={true}
                  session={session}
                />
              </div>
            </>
          </ErrorBoundary>
        )}

        {/* PROMO TOOL VIEW */}
        {currentView === 'promo' && (
          <ErrorBoundary label="Promo Tool">
            <>
              <ToolIntro toolKey="promo" open={showToolModal} onClose={() => setShowToolModal(false)} />
              <div className="content">
                <PromoTool selectedBooks={selectedBooks} bankroll={bankroll} kellyFraction={kellyFraction} session={session} />
              </div>
            </>
          </ErrorBoundary>
        )}

        {/* BET HISTORY VIEW */}
        {currentView === 'bets' && (
          <ErrorBoundary label="Bet History">
            <>
              <ToolIntro toolKey="bets" open={showToolModal} onClose={() => setShowToolModal(false)} />
              <div className="content">
                <BetHistory session={session} onNavigate={navigate} />
              </div>
            </>
          </ErrorBoundary>
        )}

        {/* GAME DETAIL VIEW */}
        {currentView === 'game' && gameEventId && (
          <ErrorBoundary label="Game Detail">
            <GameDetailPage
              eventId={gameEventId}
              sport={gameRouteState?.sport}
              eventData={gameRouteState?.eventData}
              selectedBooks={selectedBooks}
              bankroll={bankroll}
              kellyFraction={kellyFraction}
              onBack={() => navigate(-1)}
            />
          </ErrorBoundary>
        )}
      </div>

      {/* Login Modal — shown to unauthenticated users */}
      {showLoginModal && (
        <LoginPage asModal onClose={() => setShowLoginModal(false)} />
      )}

      {showContact && <ContactModal onClose={() => setShowContact(false)} />}

{/* Reset Password Modal — shown when user arrives via password reset email */}
      {isPasswordRecovery && <ResetPasswordModal />}

      {/* Complete Profile Modal — shown after Google sign-in if phone is missing */}
      {needsProfile && (
        <CompleteProfileModal
          session={session}
          onComplete={(bk, books, alertEnabled, alertThreshold, alertAllBooks) => {
            setProfileSaved(true);
            setBankroll(bk);
            setSelectedBooks(books);
            setEvBooks(books);
            setArbBooks(books);
            setArbAlertEnabled(!!alertEnabled);
            if (alertThreshold) setArbAlertThreshold(alertThreshold);
            if (alertAllBooks) setArbAlertAllBooks(true);
          }}
        />
      )}

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        selectedBooks={selectedBooks}
        bankroll={bankroll}
        kellyFraction={kellyFraction}
        arbAlertEnabled={arbAlertEnabled}
        arbAlertThreshold={arbAlertThreshold}
        arbAlertAllBooks={arbAlertAllBooks}
        session={session}
        onSave={({ books, bankroll: newBankroll, kellyFraction: newKF, arbAlertEnabled: newAlertEnabled, arbAlertThreshold: newAlertThreshold, arbAlertAllBooks: newAlertAllBooks }) => {
          const nb = Number(newBankroll) || 0;
          const nk = Number(newKF) || 0.25;
          setSelectedBooks(books);
          setEvBooks(books);
          setArbBooks(books);
          setBankroll(nb);
          setKellyFraction(nk);
          setArbAlertEnabled(!!newAlertEnabled);
          setArbAlertThreshold(newAlertThreshold ?? 1.0);
          setArbAlertAllBooks(!!newAlertAllBooks);
          if (session) {
            apiRequest('/user/profile', {
              method: 'PUT',
              body: JSON.stringify({
                bankroll: nb,
                kellyFraction: nk,
                preferredBooks: books,
                arbAlertEnabled: !!newAlertEnabled,
                arbAlertThreshold: newAlertThreshold ?? 1.0,
                arbAlertAllBooks: !!newAlertAllBooks,
              }),
            }, session);
          }
        }}
        onDeleteAccount={async () => {
          await apiRequest('/user/account', { method: 'DELETE' }, session);
          localStorage.clear();
          await signOut();
        }}
      />

      {!isLanding && (
        <footer className="app-footer">
          <div className="app-footer-inner">
            <div className="landing-footer-top">
              <img src="/navbets-logo-new.png" alt="NavBets" className="landing-logo landing-logo--footer" />
              <nav className="landing-footer-nav">
                {[
                  { label: 'Odds Dashboard', view: 'browse' },
                  { label: '+EV Finder', view: 'ev' },
                  { label: 'Arbitrage', view: 'arbitrage' },
                  { label: 'Promo Converter', view: 'promo' },
                  { label: 'My Bets', view: 'bets' },
                ].map(({ label, view }) => (
                  <button key={view} className="landing-footer-link" onClick={() => handleNavigate(view)}>
                    {label}
                  </button>
                ))}
                <button className="landing-footer-link" onClick={() => setShowContact(true)}>
                  Contact Us
                </button>
              </nav>
            </div>
            <p className="landing-footer-disclaimer">
              NavBets is for informational purposes only. We are not responsible for any financial
              losses, account restrictions, or limitations imposed by sportsbooks. Betting involves
              risk — only wager what you can afford to lose. Please gamble responsibly.
            </p>
          </div>
        </footer>
      )}
    </div>
  );
}

const SPORT_LOGOS = {
  'nba':    'https://a.espncdn.com/i/teamlogos/leagues/500/nba.png',
  'nfl':    'https://a.espncdn.com/i/teamlogos/leagues/500/nfl.png',
  'mlb':    'https://a.espncdn.com/i/teamlogos/leagues/500/mlb.png',
  'nhl':    'https://a.espncdn.com/i/teamlogos/leagues/500/nhl.png',
  'wnba':   'https://a.espncdn.com/i/teamlogos/leagues/500/wnba.png',
  'ncaab':  'https://a.espncdn.com/redesign/assets/img/icons/ESPN-icon-basketball.png',
  'ncaaf':  'https://a.espncdn.com/redesign/assets/img/icons/ESPN-icon-football-college.png',
};

const SPORT_ICONS = {
  'nfl': '🏈', 'ncaaf': '🏈', 'nba': '🏀', 'ncaab': '🏀',
  'ncaaw': '🏀', 'wnba': '🏀', 'nhl': '🏒', 'ufc': '🥊',
  'tennis': '🎾', 'soccer': '⚽', 'mlb': '⚾', 'wbc': '⚾',
  'boxing': '🥊', 'euroleague': '🏀',
};

function SportTabIcon({ sportId }) {
  const logoUrl = SPORT_LOGOS[sportId];
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={sportId}
        className="sport-logo-img"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling && (e.target.nextSibling.style.display = 'inline');
        }}
      />
    );
  }
  return <span className="sport-icon">{SPORT_ICONS[sportId] || '🏆'}</span>;
}

function SportTabs({ sports, activeSport, onChange, showHome = true }) {
  if (!sports || sports.length === 0) {
    return null;
  }

  return (
    <div className="sport-tabs">
      {showHome && (
        <>
          <button
            className={activeSport === 'home' ? 'active' : ''}
            onClick={() => onChange('home')}
          >
            <span className="sport-icon">📊</span>
            Dashboard
          </button>
          <button
            className={activeSport === 'predictions' ? 'active' : ''}
            onClick={() => onChange('predictions')}
          >
            <span className="sport-icon">🔮</span>
            Predictions
          </button>
        </>
      )}
      {!showHome && (
        <button
          className={`sport-tab-live ${activeSport === 'live' ? 'active' : ''}`}
          onClick={() => onChange('live')}
        >
          <span className="live-tab-dot" />
          Live
        </button>
      )}
      {sports.map(sport => (
        <button
          key={sport.id}
          className={activeSport === sport.id ? 'active' : ''}
          onClick={() => onChange(sport.id)}
        >
          <SportTabIcon sportId={sport.id} />
          {sport.name}
          {sport.message && <span className="sport-message">({sport.message})</span>}
        </button>
      ))}
    </div>
  );
}

export default App;
